import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Check, Car, Download, Loader2, Plus, Receipt, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { useEmpresa } from "@/hooks/useEmpresa";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GastoImagen from "@/components/GastoImagen";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ESTADO = {
  pendiente: { label: "Pendiente", cls: "border-amber-300 text-amber-700" },
  aprobado: { label: "Aprobado", cls: "border-emerald-300 text-emerald-700" },
  rechazado: { label: "Rechazado", cls: "border-rose-300 text-rose-700" },
};

const LIQUIDADO = { label: "Liquidado", cls: "border-sky-300 text-sky-700" };

function observacionesGasto(g) {
  const partes = [];
  if (g.notas?.trim()) partes.push(g.notas.trim());
  if (g.comentario_revision?.trim()) partes.push(`Revisión: ${g.comentario_revision.trim()}`);
  return partes.join(" · ");
}

function agruparKilometraje(items) {
  const map = new Map();
  for (const g of items) {
    const trabajador = g.trabajador || "Sin trabajador";
    if (!map.has(trabajador)) map.set(trabajador, []);
    map.get(trabajador).push(g);
  }
  const grupos = [...map.entries()].map(([trabajador, gastos]) => {
    gastos.sort((a, b) => (b.fecha_gasto || "").localeCompare(a.fecha_gasto || "") || (b.created_at || "").localeCompare(a.created_at || ""));
    const kmDecl = gastos.reduce((s, g) => s + (g.km_oficial ?? g.km_declarados ?? g.km ?? 0), 0);
    const kmEst = gastos.reduce((s, g) => s + (g.km_ruta_estimada ?? 0), 0);
    return { trabajador, gastos, kmDecl, kmEst };
  });
  grupos.sort((a, b) => a.trabajador.localeCompare(b.trabajador, "es"));
  return grupos;
}

function agruparGastos(items) {
  const map = new Map();
  for (const g of items) {
    const trabajador = g.trabajador || "Sin trabajador";
    const fecha = g.fecha_gasto || "";
    const key = `${trabajador}|||${fecha}`;
    if (!map.has(key)) {
      map.set(key, { trabajador, fecha, gastos: [] });
    }
    map.get(key).gastos.push(g);
  }
  const grupos = [...map.values()];
  grupos.sort((a, b) => {
    const fa = a.fecha || "0000-01-01";
    const fb = b.fecha || "0000-01-01";
    const cmpFecha = fb.localeCompare(fa);
    if (cmpFecha !== 0) return cmpFecha;
    return a.trabajador.localeCompare(b.trabajador, "es");
  });
  for (const grupo of grupos) {
    grupo.gastos.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    grupo.total = grupo.gastos.reduce((s, g) => s + (g.importe || 0), 0);
  }
  return grupos;
}

function TarjetaGasto({ gasto, onClick }) {
  const st = ESTADO[gasto.estado] || ESTADO.pendiente;
  const obs = observacionesGasto(gasto);
  return (
    <article
      className="cursor-pointer overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:border-emerald-300"
      onClick={onClick}
    >
      <div className="flex gap-4 p-4">
        <GastoImagen
          gastoId={gasto.id}
          tieneJustificante={gasto.tiene_justificante !== false}
          className="h-24 w-24 shrink-0 rounded-lg border object-cover"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-zinc-900">{gasto.proveedor || gasto.categoria_label || "Sin proveedor"}</h3>
            {gasto.categoria_label && (
              <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-700">{gasto.categoria_label}</Badge>
            )}
            <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
            {gasto.estado === "aprobado" && gasto.liquidado_trabajador && (
              <Badge variant="outline" className={`text-[10px] ${LIQUIDADO.cls}`}>{LIQUIDADO.label}</Badge>
            )}
          </div>
          <p className="mt-1 text-lg font-semibold text-emerald-700">
            {gasto.importe != null ? `${gasto.importe.toFixed(2)} ${gasto.moneda || "€"}` : "—"}
            {gasto.km != null ? ` · ${gasto.km} km` : ""}
          </p>
          {(gasto.origen_direccion || gasto.destino_direccion) && (
            <p className="text-xs text-sky-700 line-clamp-1">
              {gasto.origen_direccion} → {gasto.destino_direccion}
            </p>
          )}
          {gasto.cliente_nombre && (
            <p className="text-xs text-indigo-700">Cliente: {gasto.cliente_nombre}</p>
          )}
          {gasto.proyecto_nombre && (
            <p className="text-xs text-violet-700">#{gasto.proyecto_codigo} {gasto.proyecto_nombre}</p>
          )}
          {gasto.motivo_visita && (
            <p className="text-xs text-zinc-500">{gasto.motivo_visita}</p>
          )}
          {(gasto.proveedor_nif || gasto.numero_documento) && (
            <p className="text-xs text-zinc-500">
              {gasto.proveedor_nif && `NIF ${gasto.proveedor_nif}`}
              {gasto.proveedor_nif && gasto.numero_documento && " · "}
              {gasto.numero_documento && `Nº ${gasto.numero_documento}`}
            </p>
          )}
          {gasto.geo_alerta && (
            <p className="text-xs text-amber-700">
              GPS lejos de visita{gasto.distancia_visita_m != null ? ` (${Math.round(gasto.distancia_visita_m)} m)` : ""}
            </p>
          )}
          {obs && (
            <p className="mt-1 text-sm text-zinc-600 line-clamp-2">{obs}</p>
          )}
        </div>
      </div>
    </article>
  );
}

export default function Gastos() {
  const { empresa, cargando: cargandoEmpresa } = useEmpresa();
  const [vista, setVista] = useState("revision");
  const [filtro, setFiltro] = useState("pendiente");
  const [liquidadoFiltro, setLiquidadoFiltro] = useState("todos");
  const [proyectoFiltro, setProyectoFiltro] = useState("todos");
  const [proyectos, setProyectos] = useState([]);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [sel, setSel] = useState(null);
  const [comentario, setComentario] = useState("");
  const [revisando, setRevisando] = useState(false);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [liqForm, setLiqForm] = useState({
    usuario_id: "",
    anio: String(new Date().getFullYear()),
    mes: String(new Date().getMonth() + 1),
  });
  const [trabajadores, setTrabajadores] = useState([]);
  const [creandoLiq, setCreandoLiq] = useState(false);
  const [liqDetalle, setLiqDetalle] = useState(null);
  const [margenKm, setMargenKm] = useState(10);
  const [ajusteKm, setAjusteKm] = useState("");
  const [ajusteNota, setAjusteNota] = useState("");
  const [notaLiquidacion, setNotaLiquidacion] = useState("");
  const usaProyectos = !!empresa?.gestionar_proyectos;

  const itemsRevision = useMemo(
    () => (vista === "kilometraje" ? items : items.filter((g) => g.categoria !== "kilometraje")),
    [items, vista]
  );
  const grupos = useMemo(() => agruparGastos(itemsRevision), [itemsRevision]);
  const gruposKm = useMemo(() => agruparKilometraje(items), [items]);
  const pendientesKm = useMemo(() => items.filter((g) => g.estado === "pendiente"), [items]);
  const aprobadosSinLiquidar = useMemo(
    () => items.filter((g) => g.estado === "aprobado" && !g.liquidado_trabajador),
    [items]
  );
  const totalKmDecl = useMemo(
    () => items.reduce((s, g) => s + (g.km_oficial ?? g.km_declarados ?? g.km ?? 0), 0),
    [items]
  );
  const totalKmEst = useMemo(
    () => items.reduce((s, g) => s + (g.km_ruta_estimada ?? 0), 0),
    [items]
  );

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = {};
      if (filtro !== "todos") params.estado = filtro;
      if (liquidadoFiltro === "si") params.liquidado = "si";
      if (liquidadoFiltro === "no") params.liquidado = "no";
      if (proyectoFiltro !== "todos") params.proyecto_id = Number(proyectoFiltro);
      if (vista === "kilometraje") params.categoria = "kilometraje";
      const { data } = await api.get("/gastos", { params });
      setItems(data.items || []);
      if (data.margen_desviacion_pct != null) setMargenKm(data.margen_desviacion_pct);
      else if (data.margen_desviacion_km != null) setMargenKm(data.margen_desviacion_km);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [filtro, liquidadoFiltro, proyectoFiltro, vista]);

  const cargarLiquidaciones = useCallback(async () => {
    try {
      const { data } = await api.get("/gastos/liquidaciones", {
        params: { anio: Number(liqForm.anio) || undefined },
      });
      setLiquidaciones(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [liqForm.anio]);

  useEffect(() => {
    if (!usaProyectos) return;
    api.get("/proyectos").then((r) => setProyectos(r.data?.items || [])).catch(() => {});
  }, [usaProyectos]);

  useEffect(() => {
    api.get("/usuarios")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setTrabajadores(list.filter((u) => u.rol !== "super_admin"));
      })
      .catch(() => {});
  }, []);

  useEffect(() => { if (vista === "revision" || vista === "kilometraje") cargar(); }, [cargar, vista]);
  useEffect(() => { if (vista === "liquidaciones") cargarLiquidaciones(); }, [cargarLiquidaciones, vista]);

  const exportar = async () => {
    setExportando(true);
    try {
      const params = {};
      if (filtro !== "todos") params.estado = filtro;
      if (proyectoFiltro !== "todos") params.proyecto_id = Number(proyectoFiltro);
      const res = await api.get("/gastos/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gastos_${hoyISO()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExportando(false);
    }
  };

  const revisar = async (accion) => {
    if (!sel) return;
    setRevisando(true);
    const fd = new FormData();
    fd.append("accion", accion);
    fd.append("comentario", comentario);
    try {
      await api.post(`/gastos/${sel.id}/revisar`, fd);
      toast.success(accion === "aprobar" ? "Gasto aprobado" : "Gasto rechazado");
      setSel(null);
      setComentario("");
      cargar();
      window.dispatchEvent(new Event("notificaciones-actualizar"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevisando(false);
    }
  };

  const aceptarTodosKm = async () => {
    const ids = pendientesKm.map((g) => g.id);
    if (!ids.length) return toast.message("No hay kilometrajes pendientes");
    setRevisando(true);
    try {
      const { data } = await api.post("/gastos/revisar-lote", { ids, accion: "aprobar" });
      toast.success(`${data.total || ids.length} kilometrajes aceptados`);
      cargar();
      window.dispatchEvent(new Event("notificaciones-actualizar"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevisando(false);
    }
  };

  const ajustarKmSel = async () => {
    if (!sel) return;
    const km = Number(String(ajusteKm).replace(",", "."));
    if (!Number.isFinite(km) || km < 0) return toast.error("Indica los kilómetros ajustados");
    if (!ajusteNota.trim()) return toast.error("Deja una anotación del ajuste");
    setRevisando(true);
    try {
      const { data } = await api.post(`/gastos/${sel.id}/ajustar-km`, { km, nota: ajusteNota.trim() });
      toast.success("Kilómetros ajustados. El trabajador ha sido notificado.");
      setSel(data);
      setAjusteKm("");
      setAjusteNota("");
      cargar();
      window.dispatchEvent(new Event("notificaciones-actualizar"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevisando(false);
    }
  };

  const liquidarSel = async (liquidado = true) => {
    if (!sel) return;
    setRevisando(true);
    try {
      const { data } = await api.post(`/gastos/${sel.id}/liquidar-trabajador`, {
        liquidado,
        nota: notaLiquidacion.trim(),
      });
      toast.success(liquidado ? "Gasto marcado como liquidado al trabajador" : "Liquidación anulada");
      setSel(data);
      setNotaLiquidacion("");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevisando(false);
    }
  };

  const liquidarAprobadosLote = async () => {
    const ids = aprobadosSinLiquidar.map((g) => g.id);
    if (!ids.length) return toast.message("No hay gastos aprobados pendientes de liquidar");
    setRevisando(true);
    try {
      const { data } = await api.post("/gastos/liquidar-trabajador-lote", { ids, liquidado: true });
      toast.success(`${data.total || ids.length} gasto(s) marcados como liquidados`);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRevisando(false);
    }
  };

  const crearLiquidacion = async () => {
    if (!liqForm.usuario_id) {
      toast.error("Selecciona un trabajador");
      return;
    }
    setCreandoLiq(true);
    try {
      await api.post("/gastos/liquidaciones", {
        usuario_id: Number(liqForm.usuario_id),
        anio: Number(liqForm.anio),
        mes: Number(liqForm.mes),
      });
      toast.success("Liquidación creada");
      cargarLiquidaciones();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreandoLiq(false);
    }
  };

  const revisarLiq = async (liqId, accion) => {
    const fd = new FormData();
    fd.append("accion", accion);
    try {
      await api.post(`/gastos/liquidaciones/${liqId}/revisar`, fd);
      toast.success("Liquidación actualizada");
      setLiqDetalle(null);
      cargarLiquidaciones();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirLiq = async (id) => {
    try {
      const { data } = await api.get(`/gastos/liquidaciones/${id}`);
      setLiqDetalle(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!cargandoEmpresa && empresa && !empresa.gestionar_gastos) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Receipt}
        title={usaProyectos ? "Gastos de proyecto" : "Gastos"}
        subtitle="Revisión de tickets, kilometraje y liquidaciones mensuales"
        sectionKey="gastos"
        brandGradient={false}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="gap-1.5 border-white/40 bg-white/10 text-white hover:bg-white/20"
            onClick={exportar}
            disabled={exportando || items.length === 0 || vista !== "revision"}
          >
            {exportando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Exportar listado
          </Button>
          <Button asChild className="gap-1.5 bg-white/20 text-white hover:bg-white/30">
            <Link to="/mis-gastos"><Plus className="h-4 w-4" /> Registrar gasto</Link>
          </Button>
        </div>
      </PageHeader>

      <Tabs value={vista} onValueChange={setVista}>
        <TabsList>
          <TabsTrigger value="revision">Revisión</TabsTrigger>
          <TabsTrigger value="kilometraje">Kilometraje</TabsTrigger>
          <TabsTrigger value="liquidaciones">Liquidaciones</TabsTrigger>
        </TabsList>
      </Tabs>

      {vista === "liquidaciones" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-zinc-800">Nueva liquidación mensual</h3>
            <div className="grid gap-3 sm:grid-cols-4">
              <div>
                <Label>Trabajador</Label>
                <Select value={liqForm.usuario_id || "none"} onValueChange={(v) => setLiqForm({ ...liqForm, usuario_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar…</SelectItem>
                    {trabajadores.map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.nombre} {u.apellidos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Año</Label>
                <Input value={liqForm.anio} onChange={(e) => setLiqForm({ ...liqForm, anio: e.target.value })} />
              </div>
              <div>
                <Label>Mes</Label>
                <Input type="number" min={1} max={12} value={liqForm.mes} onChange={(e) => setLiqForm({ ...liqForm, mes: e.target.value })} />
              </div>
              <div className="flex items-end">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={creandoLiq} onClick={crearLiquidacion}>
                  {creandoLiq ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generar"}
                </Button>
              </div>
            </div>
            <p className="mt-2 text-xs text-zinc-500">Agrupa los gastos aprobados del periodo que aún no estén liquidados.</p>
          </div>

          {liquidaciones.length === 0 ? (
            <p className="rounded-xl border border-dashed border-zinc-200 py-12 text-center text-sm text-zinc-500">
              No hay liquidaciones para este año.
            </p>
          ) : (
            <ul className="space-y-2">
              {liquidaciones.map((l) => (
                <li key={l.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left hover:border-emerald-300"
                    onClick={() => abrirLiq(l.id)}
                  >
                    <div>
                      <p className="font-medium text-zinc-900">{l.trabajador} · {l.periodo}</p>
                      <p className="text-xs text-zinc-500">{l.num_gastos} gastos · {l.total_km} km</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-700">{Number(l.total).toFixed(2)} €</p>
                      <Badge variant="outline" className="text-[10px]">{l.estado}</Badge>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : vista === "kilometraje" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Tabs value={filtro} onValueChange={setFiltro}>
                <TabsList>
                  <TabsTrigger value="pendiente">Pendientes</TabsTrigger>
                  <TabsTrigger value="aprobado">Aprobados</TabsTrigger>
                  <TabsTrigger value="rechazado">Rechazados</TabsTrigger>
                  <TabsTrigger value="todos">Todos</TabsTrigger>
                </TabsList>
              </Tabs>
              <Select value={liquidadoFiltro} onValueChange={setLiquidadoFiltro}>
                <SelectTrigger className="w-44"><SelectValue placeholder="Liquidación" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Liquidación: todos</SelectItem>
                  <SelectItem value="no">Sin liquidar</SelectItem>
                  <SelectItem value="si">Liquidados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={revisando || aprobadosSinLiquidar.length === 0}
                onClick={liquidarAprobadosLote}
              >
                {revisando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
                <span className="ml-1">Liquidar aprobados ({aprobadosSinLiquidar.length})</span>
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={revisando || pendientesKm.length === 0}
                onClick={aceptarTodosKm}
              >
                {revisando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                <span className="ml-1">Aceptar todos los pendientes ({pendientesKm.length})</span>
              </Button>
            </div>
          </div>
          <p className="text-xs text-zinc-500">
            Se marca en ámbar cuando la diferencia entre km indicados y km de ruta supera el {margenKm} % configurado (ajustes de empresa).
          </p>
          {cargando ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
          ) : items.length === 0 ? (
            <p className="rounded-xl border border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-500">
              No hay registros de kilometraje con este filtro.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                    <th className="px-3 py-2">Fecha</th>
                    <th className="px-3 py-2">Trabajador</th>
                    <th className="px-3 py-2 text-right">Km indicados</th>
                    <th className="px-3 py-2 text-right">Km ruta</th>
                    <th className="px-3 py-2 text-right">Variación</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Liquidación</th>
                  </tr>
                </thead>
                <tbody>
                  {gruposKm.map((grupo) => (
                    grupo.gastos.map((g) => {
                      const varKm = g.km_variacion;
                      const desvia = Boolean(g.desvia_margen);
                      const st = ESTADO[g.estado] || ESTADO.pendiente;
                      return (
                        <tr
                          key={g.id}
                          className={`cursor-pointer border-b border-zinc-50 last:border-0 hover:bg-zinc-50 ${desvia ? "bg-amber-50" : ""}`}
                          onClick={() => {
                            setSel(g);
                            setComentario("");
                            setAjusteKm(String(g.km_oficial ?? g.km ?? "").replace(".", ","));
                            setAjusteNota(g.ajuste_nota || "");
                            setNotaLiquidacion(g.nota_liquidacion || "");
                          }}
                        >
                          <td className="px-3 py-2 whitespace-nowrap">{g.fecha_gasto ? fmtFecha(g.fecha_gasto) : "—"}</td>
                          <td className="px-3 py-2 font-medium">{g.trabajador}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {(g.km_oficial ?? g.km_declarados ?? g.km) != null
                              ? Number(g.km_oficial ?? g.km_declarados ?? g.km).toFixed(1)
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {g.km_ruta_estimada != null ? Number(g.km_ruta_estimada).toFixed(1) : "—"}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums font-semibold ${desvia ? "text-amber-800" : "text-zinc-700"}`}>
                            {varKm == null ? "—" : `${varKm > 0 ? "+" : ""}${Number(varKm).toFixed(1)}`}
                            {desvia && <span className="ml-1 text-[10px] font-normal">no cuadra</span>}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            {g.estado === "aprobado" ? (
                              g.liquidado_trabajador ? (
                                <Badge variant="outline" className={`text-[10px] ${LIQUIDADO.cls}`}>Liquidado</Badge>
                              ) : (
                                <span className="text-[11px] text-zinc-400">Pendiente</span>
                              )
                            ) : "—"}
                          </td>
                        </tr>
                      );
                    })
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-zinc-200 bg-zinc-50 font-semibold">
                    <td className="px-3 py-2" colSpan={2}>Total</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totalKmDecl.toFixed(1)} km</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totalKmEst.toFixed(1)} km</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {(totalKmDecl - totalKmEst).toFixed(1)}
                    </td>
                    <td />
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={filtro} onValueChange={setFiltro}>
          <TabsList>
            <TabsTrigger value="pendiente">Pendientes</TabsTrigger>
            <TabsTrigger value="aprobado">Aprobados</TabsTrigger>
            <TabsTrigger value="rechazado">Rechazados</TabsTrigger>
            <TabsTrigger value="todos">Todos</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={liquidadoFiltro} onValueChange={setLiquidadoFiltro}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Liquidación" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Liquidación: todos</SelectItem>
            <SelectItem value="no">Sin liquidar</SelectItem>
            <SelectItem value="si">Liquidados</SelectItem>
          </SelectContent>
        </Select>
        {(filtro === "aprobado" || filtro === "todos") && (
          <Button
            variant="outline"
            size="sm"
            disabled={revisando || aprobadosSinLiquidar.length === 0}
            onClick={liquidarAprobadosLote}
          >
            {revisando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
            <span className="ml-1">Liquidar aprobados ({aprobadosSinLiquidar.length})</span>
          </Button>
        )}
        {usaProyectos && (
          <Select value={proyectoFiltro} onValueChange={setProyectoFiltro}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Proyecto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los proyectos</SelectItem>
              {proyectos.map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>#{p.codigo} {p.nombre}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {cargando ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
      ) : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-500">
          No hay gastos con este filtro.
        </p>
      ) : (
        <div className="space-y-6">
          {grupos.map((grupo) => (
            <section key={`${grupo.trabajador}-${grupo.fecha}`} className="space-y-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-zinc-200 pb-2">
                <div>
                  <h2 className="text-base font-semibold text-zinc-900">{grupo.trabajador}</h2>
                  <p className="text-sm text-zinc-500">
                    {grupo.fecha ? fmtFecha(grupo.fecha) : "Sin fecha"}
                    {" · "}
                    {grupo.gastos.length} gasto{grupo.gastos.length === 1 ? "" : "s"}
                  </p>
                </div>
                {grupo.total > 0 && (
                  <p className="text-sm font-semibold text-emerald-700">
                    Total: {grupo.total.toFixed(2)} €
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-3">
                {grupo.gastos.map((g) => (
                  <TarjetaGasto
                    key={g.id}
                    gasto={g}
                    onClick={() => { setSel(g); setComentario(""); setNotaLiquidacion(g.nota_liquidacion || ""); }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
        </>
      )}

      {sel && (
        <Dialog open onOpenChange={() => setSel(null)}>
          <DialogContent className={sel.categoria === "kilometraje" ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-lg"}>
            <DialogHeader>
              <DialogTitle>{sel.categoria === "kilometraje" ? "Kilometraje del día" : "Revisar gasto"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {sel.categoria === "kilometraje" ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="mb-1 text-[11px] text-zinc-500">Cuentakilómetros inicial</p>
                    <GastoImagen
                      gastoId={sel.id}
                      cual="inicio"
                      tieneJustificante={sel.tiene_foto_inicio !== false && sel.tiene_justificante !== false}
                      className="mx-auto max-h-40 w-full rounded-lg border object-contain"
                      alt="Odómetro inicio"
                    />
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] text-zinc-500">Cuentakilómetros final</p>
                    <GastoImagen
                      gastoId={sel.id}
                      cual="fin"
                      tieneJustificante={!!sel.tiene_foto_fin}
                      className="mx-auto max-h-40 w-full rounded-lg border object-contain"
                      alt="Odómetro fin"
                    />
                  </div>
                </div>
              ) : (
              <GastoImagen
                gastoId={sel.id}
                tieneJustificante={sel.tiene_justificante !== false}
                className="mx-auto max-h-64 rounded-lg border object-contain"
              />
              )}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-zinc-500">Trabajador</span><p className="font-medium">{sel.trabajador}</p></div>
                <div><span className="text-zinc-500">Importe total</span><p className="font-medium">{sel.importe != null ? `${sel.importe.toFixed(2)} €` : "—"}</p></div>
                <div><span className="text-zinc-500">Categoría</span><p className="font-medium">{sel.categoria_label || sel.categoria || "—"}</p></div>
                <div><span className="text-zinc-500">Fecha</span><p className="font-medium">{sel.fecha_gasto ? fmtFecha(sel.fecha_gasto) : "—"}</p></div>
                <div><span className="text-zinc-500">Proveedor</span><p className="font-medium">{sel.proveedor || "—"}</p></div>
                <div><span className="text-zinc-500">NIF/CIF</span><p className="font-medium">{sel.proveedor_nif || "—"}</p></div>
                {(sel.origen_direccion || sel.destino_direccion || sel.recorrido_km) && (
                  <div className="col-span-2">
                    <span className="text-zinc-500">Recorrido</span>
                    <p className="font-medium">
                      {(sel.recorrido_km?.origen?.direccion
                        || sel.recorrido_km?.origen?.nombre
                        || sel.origen_direccion
                        || "Inicio")}
                      {" → "}
                      {(sel.recorrido_km?.destino?.direccion
                        || sel.recorrido_km?.destino?.nombre
                        || sel.destino_direccion
                        || "Fin")}
                    </p>
                  </div>
                )}
                {sel.categoria === "kilometraje" && (
                  <div className="col-span-2 rounded-lg border border-sky-100 bg-sky-50/50 p-3 text-sm space-y-1">
                    <p>
                      Km indicados: <strong>{sel.km_declarados ?? sel.km ?? "—"}</strong>
                      {sel.km_ajustados != null ? ` · ajustados a ${sel.km_ajustados}` : ""}
                      {sel.km_ruta_estimada != null ? ` · ruta ≈ ${sel.km_ruta_estimada} km` : ""}
                      {sel.km_variacion != null ? ` · var. ${sel.km_variacion > 0 ? "+" : ""}${sel.km_variacion}` : ""}
                    </p>
                    {sel.recorrido_km?.origen && (
                      <p className="text-xs text-zinc-600">
                        Inicio:{" "}
                        {sel.recorrido_km.origen.direccion
                          || sel.recorrido_km.origen.nombre
                          || "—"}
                        {sel.recorrido_km.origen.hora ? ` · ${sel.recorrido_km.origen.hora}` : ""}
                        {sel.recorrido_km.origen.fuente === "primer_picado"
                          ? " (GPS del primer picado)"
                          : sel.recorrido_km.origen.fuente === "centro"
                            ? " (centro de trabajo)"
                            : ""}
                      </p>
                    )}
                    {(sel.recorrido_km?.paradas || sel.visitas_km || []).map((p, i) => (
                      <p key={i} className="text-xs text-zinc-600">
                        {String(i + 1).padStart(2, "0")}. {p.nombre || p.direccion}
                        {p.es_potencial && (
                          <span className="ml-1.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-800">
                            Potencial
                          </span>
                        )}
                      </p>
                    ))}
                    {sel.recorrido_km?.destino && (
                      <p className={`text-xs ${sel.recorrido_km.destino.fuente === "pendiente_salida" || sel.recorrido_km.destino.registrado === false ? "text-amber-700 font-medium" : "text-zinc-600"}`}>
                        Final:{" "}
                        {sel.recorrido_km.destino.fuente === "pendiente_salida" || sel.recorrido_km.destino.registrado === false
                          ? (sel.recorrido_km.destino.direccion || sel.recorrido_km.destino.nombre || "Sin registrar fin de tránsito")
                          : (sel.recorrido_km.destino.direccion || sel.recorrido_km.destino.nombre || "—")}
                        {sel.recorrido_km.destino.hora && sel.recorrido_km.destino.fuente !== "pendiente_salida"
                          ? ` · ${sel.recorrido_km.destino.hora}`
                          : ""}
                        {sel.recorrido_km.destino.fuente === "salida"
                          ? " (GPS de la salida)"
                          : sel.recorrido_km.destino.fuente === "centro"
                            ? " (centro de trabajo)"
                            : ""}
                      </p>
                    )}
                    {(sel.recorrido_km?.tramos || []).filter((t) => t.km != null).map((t, i) => (
                      <p key={`t${i}`} className="text-[11px] text-sky-800">
                        {t.desde_nombre} → {t.hasta_nombre}: {Number(t.km).toFixed(1)} km
                      </p>
                    ))}
                    {sel.ocr_odometro_inicio != null && (
                      <p className="text-[11px] text-zinc-500">
                        OCR: inicio {sel.ocr_odometro_inicio}
                        {sel.ocr_odometro_fin != null ? ` · fin ${sel.ocr_odometro_fin}` : ""}
                      </p>
                    )}
                    {sel.ajuste_nota && (
                      <p className="text-xs text-violet-800">Ajuste: {sel.ajuste_nota} {sel.ajustado_por ? `(${sel.ajustado_por})` : ""}</p>
                    )}
                  </div>
                )}
                {sel.geo_alerta && (
                  <div className="col-span-2 text-amber-700 text-xs">
                    Alerta GPS: lejos de la visita{sel.distancia_visita_m != null ? ` (${Math.round(sel.distancia_visita_m)} m)` : ""}
                  </div>
                )}
                <div><span className="text-zinc-500">Nº documento</span><p className="font-medium">{sel.numero_documento || "—"}</p></div>
                <div className="col-span-2"><span className="text-zinc-500">Dirección</span><p className="font-medium">{sel.proveedor_direccion || "—"}</p></div>
                <div><span className="text-zinc-500">Población</span><p className="font-medium">{sel.proveedor_poblacion || "—"}</p></div>
                <div><span className="text-zinc-500">Provincia</span><p className="font-medium">{sel.proveedor_provincia || "—"}</p></div>
                <div><span className="text-zinc-500">C.P.</span><p className="font-medium">{sel.proveedor_codigo_postal || "—"}</p></div>
                <div><span className="text-zinc-500">Teléfono</span><p className="font-medium">{sel.proveedor_telefono || "—"}</p></div>
              </div>
              {sel.ivas?.length > 0 && (
                <div className="rounded-lg border border-zinc-200 overflow-hidden text-sm">
                  <table className="w-full">
                    <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                      <tr>
                        <th className="px-3 py-2 text-left">IVA</th>
                        <th className="px-3 py-2 text-right">Base</th>
                        <th className="px-3 py-2 text-right">Cuota</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sel.ivas.map((r, i) => (
                        <tr key={i} className="border-t border-zinc-100">
                          <td className="px-3 py-2">{r.porcentaje}%</td>
                          <td className="px-3 py-2 text-right">{r.base} €</td>
                          <td className="px-3 py-2 text-right">{r.cuota} €</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {sel.proyecto_nombre && (
                <p className="text-sm text-violet-800">Proyecto: #{sel.proyecto_codigo} {sel.proyecto_nombre}</p>
              )}
              {sel.notas && <p className="text-sm text-zinc-600"><span className="font-medium">Notas:</span> {sel.notas}</p>}
              {sel.comentario_revision && (
                <p className="text-sm text-zinc-600"><span className="font-medium">Comentario revisión:</span> {sel.comentario_revision}</p>
              )}
              {sel.estado === "aprobado" && sel.liquidado_trabajador && (
                <div className="rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-2 text-sm text-sky-900">
                  <p className="font-medium">Liquidado al trabajador</p>
                  {sel.liquidado_por && (
                    <p className="text-xs text-sky-800">
                      Por {sel.liquidado_por}
                      {sel.liquidado_en ? ` · ${fmtFecha(sel.liquidado_en.slice(0, 10))}` : ""}
                    </p>
                  )}
                  {sel.nota_liquidacion && (
                    <p className="text-xs text-sky-800 mt-1">{sel.nota_liquidacion}</p>
                  )}
                </div>
              )}
              {sel.estado === "aprobado" && (
                <div className="rounded-lg border border-sky-100 bg-sky-50/40 p-3 space-y-2">
                  <p className="text-xs font-semibold text-sky-900">Liquidación al trabajador</p>
                  {!sel.liquidado_trabajador && (
                    <div>
                      <Label>Nota (opcional)</Label>
                      <Textarea
                        rows={2}
                        value={notaLiquidacion}
                        onChange={(e) => setNotaLiquidacion(e.target.value)}
                        placeholder="Transferencia, nómina, fecha de pago…"
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {!sel.liquidado_trabajador ? (
                      <Button type="button" className="bg-sky-600 hover:bg-sky-700 gap-1" disabled={revisando} onClick={() => liquidarSel(true)}>
                        {revisando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
                        Marcar como liquidado
                      </Button>
                    ) : (
                      <Button type="button" variant="outline" disabled={revisando} onClick={() => liquidarSel(false)}>
                        Quitar liquidación
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {sel.estado === "pendiente" && (
                <div>
                  <Label>Comentario (opcional)</Label>
                  <Textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} />
                </div>
              )}
              {sel.categoria === "kilometraje" && sel.estado !== "rechazado" && (
                <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3 space-y-2">
                  <p className="text-xs font-semibold text-violet-900">Ajustar kilómetros (sin aprobación del trabajador)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Km ajustados</Label>
                      <Input value={ajusteKm} onChange={(e) => setAjusteKm(e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label>Anotación</Label>
                      <Textarea rows={2} value={ajusteNota} onChange={(e) => setAjusteNota(e.target.value)} placeholder="Motivo del ajuste…" />
                    </div>
                  </div>
                  <Button type="button" variant="outline" disabled={revisando} onClick={ajustarKmSel}>
                    Guardar ajuste y notificar
                  </Button>
                </div>
              )}
            </div>
            {sel.estado === "pendiente" ? (
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" className="text-rose-600" disabled={revisando} onClick={() => revisar("rechazar")}>
                  <X className="mr-1 h-4 w-4" /> Rechazar
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={revisando} onClick={() => revisar("aprobar")}>
                  <Check className="mr-1 h-4 w-4" /> Aprobar
                </Button>
              </DialogFooter>
            ) : (
              <DialogFooter>
                <Button variant="outline" onClick={() => setSel(null)}>Cerrar</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {liqDetalle && (
        <Dialog open onOpenChange={() => setLiqDetalle(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Liquidación {liqDetalle.periodo}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p><span className="text-zinc-500">Trabajador:</span> <strong>{liqDetalle.trabajador}</strong></p>
              <p><span className="text-zinc-500">Estado:</span> {liqDetalle.estado}</p>
              <p><span className="text-zinc-500">Total:</span> <strong>{Number(liqDetalle.total).toFixed(2)} €</strong> · {liqDetalle.num_gastos} gastos · {liqDetalle.total_km} km</p>
              <ul className="max-h-48 space-y-1 overflow-auto rounded-lg border border-zinc-100 p-2">
                {(liqDetalle.gastos || []).map((g) => (
                  <li key={g.id} className="flex justify-between gap-2 text-xs">
                    <span className="truncate">{g.categoria_label || g.categoria} · {g.proveedor || "—"}</span>
                    <span className="shrink-0 font-medium">{g.importe != null ? `${Number(g.importe).toFixed(2)} €` : "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              {["enviada", "borrador"].includes(liqDetalle.estado) && (
                <>
                  <Button variant="outline" className="text-rose-600" onClick={() => revisarLiq(liqDetalle.id, "rechazar")}>Rechazar</Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => revisarLiq(liqDetalle.id, "aprobar")}>Aprobar</Button>
                </>
              )}
              {liqDetalle.estado === "aprobada" && (
                <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => revisarLiq(liqDetalle.id, "pagada")}>Marcar pagada</Button>
              )}
              <Button variant="outline" onClick={() => setLiqDetalle(null)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
